import type { FC } from "react";
import type { Doc } from "sharedb/lib/client";

import type {
  AssetFieldType,
  PdfState,
  WorksheetType,
} from "../../../../../shared/types/worksheet";
import { BLOCKS_PATH } from "../../../../../shared/worksheets/constants";
import { PdfPageToolbar } from "../../../../../worksheets/components/Field/PdfPageToolbar";
import { ZoomToolbar } from "../../../../../worksheets/components/Field/ZoomToolbar";
import { isPDFFile } from "../../../../../worksheets/helpers/files";

import { LegacyButton } from "mds/components/LegacyButton";
import { ArrowDownIcon, PresentPlayOutlineIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectFeaturingPdfState } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { getDefaultPdfState, setPdfStateOnDoc } from "utils/asset";
import { usePresentation } from "utils/presentation";

type FeatureHeaderProps = {
  doc: Doc<WorksheetType>;
  blockIndex: number;
};

export const FeatureAssetHeader: FC<FeatureHeaderProps> = ({ doc, blockIndex }) => {
  const assetField = doc.data?.b?.[blockIndex]?.f[0] as AssetFieldType;
  const { name, size, fileType, url, pdfState } = assetField;
  const { isPresenting, isFollowing } = usePresentation();
  const featuringPdfState = useAppSelector(selectFeaturingPdfState);

  const currentPdfState = isPresenting ? pdfState || getDefaultPdfState() : featuringPdfState;
  const isPdf = isPDFFile(fileType);
  const shortName = name.length > 50 ? `${name.slice(0, 50)}...` : name;
  const dispatch = useAppDispatch();

  const onPdfStateChange = (newState: Partial<PdfState>) => {
    if (isPresenting) {
      setPdfStateOnDoc(doc, [BLOCKS_PATH, blockIndex, "f", 0], pdfState, newState);
    } else {
      dispatch(viewStateActions.setFeaturingPdfState(newState));
    }
  };

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <PresentPlayOutlineIcon className="text-green" />
        <span className="break-words pr-2">
          <span className="mr-2">{shortName}</span>
          <span className="body-s whitespace-nowrap text-black-tint-70">{`(${size})`}</span>
        </span>
      </div>

      <div className="flex items-center gap-2">
        {isPdf && (
          <>
            <ZoomToolbar
              setZoom={(zoom) => onPdfStateChange({ zoom, userUpdated: isFollowing })}
              zoom={currentPdfState.zoom}
            />

            <PdfPageToolbar
              pdfState={currentPdfState}
              setPdfState={(state) => onPdfStateChange({ ...state, userUpdated: isFollowing })}
            />
          </>
        )}

        <LegacyButton download={name} kind="secondary" size="s" to={url || ""} iconOnly>
          <ArrowDownIcon />
        </LegacyButton>
      </div>
    </div>
  );
};
